import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../utils/auth';
import { authApi } from '../../api';

// Replace hardcoded localhost images with proper placeholders

const ProfileContainer = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
  background: #ffffff;
  border-radius: 8px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  
  @media (max-width: 768px) {
    border-radius: 0;
    min-height: 100vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const ProfileContent = styled.div`
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
    min-height: calc(100vh - 32px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    min-height: calc(100vh - 24px);
  }
`;

const TitleSection = styled.div`
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const Title = styled.h1`
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 22px;
  font-weight: 400;
  line-height: 28px;
  color: #2f3941;
  margin: 0 0 8px 0;
  
  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 26px;
  }
`;

const Subtitle = styled.p`
  font-family: 'SF Pro', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #68737d;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 18px;
  }
`;

const TabContainer = styled.div`
  border-bottom: 1px solid #d8dcde;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const TabList = styled.div`
  display: flex;
  gap: 0;
  
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const TabButton = styled.button<{ isActive: boolean }>`
  padding: 10px 28px;
  border: none;
  background: none;
  border-bottom: 3px solid ${props => props.isActive ? '#8d4f1b' : 'transparent'};
  color: ${props => props.isActive ? '#8d4f1b' : '#68737d'};
  font-weight: ${props => props.isActive ? '590' : '400'};
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  font-family: 'SF Pro', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;

  &:hover {
    color: #8d4f1b;
  }
  
  @media (max-width: 768px) {
    padding: 8px 20px;
    font-size: 13px;
    line-height: 18px;
  }
`;

const TabContent = styled.div`
  padding: 0;
  
  @media (max-width: 768px) {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    max-height: calc(100vh - 200px);
  }
  
  @media (max-width: 480px) {
    max-height: calc(100vh - 180px);
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const ProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ProfileAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-weight: bold;
  font-size: 16px;
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 14px;
  }
`;

const ProfileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProfileName = styled.h3`
  font-family: 'SF Pro', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 24px;
  color: #2f3941;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 22px;
  }
`;

const ProfileRole = styled.p`
  font-family: 'SF Pro', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #68737d;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 11px;
    line-height: 14px;
  }
`;

const EditButton = styled.button`
  background: none;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  padding: 8px 12px;
  color: #2f3941;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 10px;
    font-size: 13px;
  }
`;

const FormSection = styled.div`
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const SectionTitle = styled.h3`
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 16px;
  font-weight: 590;
  line-height: 22px;
  color: #2f3941;
  margin: 0 0 16px 0;
  
  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 20px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const FieldLabel = styled.label`
  font-family: 'SF Pro', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #68737d;
  
  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 18px;
  }
`;

const FieldInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #2f3941;
  
  &:focus {
    outline: none;
    border-color: #8d4f1b;
  }
  
  &:disabled {
    background: #f8f9fa;
    color: #68737d;
  }
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 13px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const PrimaryButton = styled.button`
  background: #8d4f1b;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #703815;
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 13px;
  }
`;

const SecondaryButton = styled.button`
  background: none;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 400;
  color: #2f3941;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 13px;
  }
`;

// Password & Security Tab Styles
const PasswordSection = styled.div`
  width: 100%;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const PasswordForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const PasswordField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const PasswordInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #2f3941;
  
  &:focus {
    outline: none;
    border-color: #8d4f1b;
  }
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 13px;
  }
`;

// Preferences Tab Styles
const PreferencesSection = styled.div`
  width: 100%;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const PreferenceGroup = styled.div`
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const PreferenceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    padding: 10px 0;
  }
`;

const PreferenceLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  @media (max-width: 768px) {
    gap: 2px;
  }
`;

const PreferenceTitle = styled.span`
  font-family: 'SF Pro', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #2f3941;
  
  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 18px;
  }
`;

const PreferenceDescription = styled.span`
  font-family: 'SF Pro', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #68737d;
  
  @media (max-width: 768px) {
    font-size: 11px;
    line-height: 14px;
  }
`;

const Toggle = styled.input`
  appearance: none;
  width: 40px;
  height: 20px;
  border-radius: 20px;
  background: #d8dcde;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:checked {
    background: #8d4f1b;
  }
  
  &:before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    top: 2px;
    left: 2px;
    transition: all 0.2s ease;
  }
  
  &:checked:before {
    left: 22px;
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 18px;
    
    &:before {
      width: 14px;
      height: 14px;
    }
    
    &:checked:before {
      left: 20px;
    }
  }
`;

const SelectField = styled.select`
  padding: 8px 12px;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #2f3941;
  background: white;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #8d4f1b;
  }
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 13px;
    min-width: 120px;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const SuccessMessage = styled.div`
  color: #155724;
  font-size: 14px;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'information' | 'password' | 'preferences'>('information');
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialty: '',
    institution: '',
    registration: '',
    experience: ''
  });

  // Initialize profile data from user context
  useEffect(() => {
    if (user) {
      const nameParts = user.name?.split(' ') || ['', ''];
      setProfileData({
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: user.email || '',
        phone: user.profile?.phone || '',
        specialty: user.profile?.specialization || '',
        institution: user.profile?.hospital || '',
        registration: user.profile?.licenseNumber || '',
        experience: user.profile?.bio || ''
      });
    }
  }, [user]);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const [preferencesData, setPreferencesData] = useState({
    emailNotifications: true,
    pushNotifications: false,
    weeklyDigest: true,
    language: 'en',
    timezone: 'Africa/Lagos',
    theme: 'light'
  });

  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  const handleSaveProfile = async () => {
    try {
      setProfileError('');
      setProfileSuccess('');
      await authApi.updateProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phone: profileData.phone,
        specialization: profileData.specialty,
        licenseNumber: profileData.registration,
        hospital: profileData.institution,
        bio: profileData.experience,
      });
      setProfileSuccess('Profile updated successfully');
      setIsEditing(false);
    } catch (error: any) {
      setProfileError(error.message || 'Failed to update profile');
      console.error('Failed to update profile:', error);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }
    
    try {
      setIsUpdatingPassword(true);
      setPasswordError('');
      setPasswordSuccess('');
      
      await authApi.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      
      setPasswordSuccess('Password updated successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      setPasswordError(error.message || 'Failed to update password');
      console.error('Failed to update password:', error);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handlePreferencesChange = () => {
    console.log('Saving preferences:', preferencesData);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'information':
        return (
          <>
            <ProfileHeader>
              <ProfileInfo>
                <ProfileAvatar>
                  {user?.name ? user.name.charAt(0).toUpperCase() : '?'}
                </ProfileAvatar>
                <ProfileDetails>
                  <ProfileName>{user?.name || 'User Name'}</ProfileName>
                  <ProfileRole>{user?.role ? (user.role.charAt(0).toUpperCase() + user.role.slice(1)) : 'Role'}</ProfileRole>
                </ProfileDetails>
              </ProfileInfo>
              <EditButton onClick={() => setIsEditing(!isEditing)}>
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </EditButton>
            </ProfileHeader>

            <FormSection>
              <SectionTitle>Personal Information</SectionTitle>
              <FormGrid>
                <FormField>
                  <FieldLabel>First Name</FieldLabel>
                  <FieldInput
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
                    disabled={!isEditing}
                  />
                </FormField>
                <FormField>
                  <FieldLabel>Last Name</FieldLabel>
                  <FieldInput
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
                    disabled={!isEditing}
                  />
                </FormField>
                <FormField>
                  <FieldLabel>Email Address</FieldLabel>
                  <FieldInput
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                    disabled={!isEditing}
                  />
                </FormField>
                <FormField>
                  <FieldLabel>Phone Number</FieldLabel>
                  <FieldInput
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                    disabled={!isEditing}
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>Professional Information</SectionTitle>
              <FormGrid>
                <FormField>
                  <FieldLabel>Specialty</FieldLabel>
                  <FieldInput
                    type="text"
                    value={profileData.specialty}
                    onChange={(e) => setProfileData({...profileData, specialty: e.target.value})}
                    disabled={!isEditing}
                  />
                </FormField>
                <FormField>
                  <FieldLabel>Institution</FieldLabel>
                  <FieldInput
                    type="text"
                    value={profileData.institution}
                    onChange={(e) => setProfileData({...profileData, institution: e.target.value})}
                    disabled={!isEditing}
                  />
                </FormField>
                <FormField>
                  <FieldLabel>Professional Registration</FieldLabel>
                  <FieldInput
                    type="text"
                    value={profileData.registration}
                    onChange={(e) => setProfileData({...profileData, registration: e.target.value})}
                    disabled={!isEditing}
                  />
                </FormField>
                <FormField>
                  <FieldLabel>Years of Experience</FieldLabel>
                  <FieldInput
                    type="number"
                    value={profileData.experience}
                    onChange={(e) => setProfileData({...profileData, experience: e.target.value})}
                    disabled={!isEditing}
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            {isEditing && (
              <>
                {profileError && <ErrorMessage>{profileError}</ErrorMessage>}
                {profileSuccess && <SuccessMessage>{profileSuccess}</SuccessMessage>}
                <ButtonGroup>
                  <SecondaryButton onClick={() => setIsEditing(false)}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton onClick={handleSaveProfile}>
                    Save Changes
                  </PrimaryButton>
                </ButtonGroup>
              </>
            )}
          </>
        );

      case 'password':
        return (
          <PasswordSection>
            <FormSection>
              <SectionTitle>Change Password</SectionTitle>
              <PasswordForm onSubmit={handlePasswordChange}>
                <PasswordField>
                  <FieldLabel>Current Password</FieldLabel>
                  <PasswordInput
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    placeholder="Enter current password"
                    required
                  />
                </PasswordField>
                <PasswordField>
                  <FieldLabel>New Password</FieldLabel>
                  <PasswordInput
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    placeholder="Enter new password"
                    required
                  />
                </PasswordField>
                <PasswordField>
                  <FieldLabel>Confirm New Password</FieldLabel>
                  <PasswordInput
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    placeholder="Confirm new password"
                    required
                  />
                </PasswordField>
                
                <>
                  {passwordError && <ErrorMessage>{passwordError}</ErrorMessage>}
                  {passwordSuccess && <SuccessMessage>{passwordSuccess}</SuccessMessage>}
                  
                  <ButtonGroup>
                    <PrimaryButton type="submit" disabled={isUpdatingPassword}>
                      {isUpdatingPassword ? 'Updating...' : 'Update Password'}
                    </PrimaryButton>
                  </ButtonGroup>
                </>
              </PasswordForm>
            </FormSection>
          </PasswordSection>
        );

      case 'preferences':
        return (
          <PreferencesSection>
            <FormSection>
              <SectionTitle>Notification Preferences</SectionTitle>
              <PreferenceGroup>
                <PreferenceItem>
                  <PreferenceLabel>
                    <PreferenceTitle>Email Notifications</PreferenceTitle>
                    <PreferenceDescription>Receive email notifications for new cases and updates</PreferenceDescription>
                  </PreferenceLabel>
                  <Toggle
                    type="checkbox"
                    checked={preferencesData.emailNotifications}
                    onChange={(e) => setPreferencesData({...preferencesData, emailNotifications: e.target.checked})}
                  />
                </PreferenceItem>
                <PreferenceItem>
                  <PreferenceLabel>
                    <PreferenceTitle>Push Notifications</PreferenceTitle>
                    <PreferenceDescription>Receive push notifications on your device</PreferenceDescription>
                  </PreferenceLabel>
                  <Toggle
                    type="checkbox"
                    checked={preferencesData.pushNotifications}
                    onChange={(e) => setPreferencesData({...preferencesData, pushNotifications: e.target.checked})}
                  />
                </PreferenceItem>
                <PreferenceItem>
                  <PreferenceLabel>
                    <PreferenceTitle>Weekly Digest</PreferenceTitle>
                    <PreferenceDescription>Get a weekly summary of your activity</PreferenceDescription>
                  </PreferenceLabel>
                  <Toggle
                    type="checkbox"
                    checked={preferencesData.weeklyDigest}
                    onChange={(e) => setPreferencesData({...preferencesData, weeklyDigest: e.target.checked})}
                  />
                </PreferenceItem>
              </PreferenceGroup>
            </FormSection>

            <FormSection>
              <SectionTitle>General Settings</SectionTitle>
              <PreferenceGroup>
                <PreferenceItem>
                  <PreferenceLabel>
                    <PreferenceTitle>Language</PreferenceTitle>
                    <PreferenceDescription>Choose your preferred language</PreferenceDescription>
                  </PreferenceLabel>
                  <SelectField
                    value={preferencesData.language}
                    onChange={(e) => setPreferencesData({...preferencesData, language: e.target.value})}
                  >
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="es">Spanish</option>
                  </SelectField>
                </PreferenceItem>
                <PreferenceItem>
                  <PreferenceLabel>
                    <PreferenceTitle>Timezone</PreferenceTitle>
                    <PreferenceDescription>Set your local timezone</PreferenceDescription>
                  </PreferenceLabel>
                  <SelectField
                    value={preferencesData.timezone}
                    onChange={(e) => setPreferencesData({...preferencesData, timezone: e.target.value})}
                  >
                    <option value="Africa/Lagos">Africa/Lagos</option>
                    <option value="Europe/London">Europe/London</option>
                    <option value="America/New_York">America/New_York</option>
                  </SelectField>
                </PreferenceItem>
                <PreferenceItem>
                  <PreferenceLabel>
                    <PreferenceTitle>Theme</PreferenceTitle>
                    <PreferenceDescription>Choose your preferred theme</PreferenceDescription>
                  </PreferenceLabel>
                  <SelectField
                    value={preferencesData.theme}
                    onChange={(e) => setPreferencesData({...preferencesData, theme: e.target.value})}
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto</option>
                  </SelectField>
                </PreferenceItem>
              </PreferenceGroup>
            </FormSection>

            <ButtonGroup>
              <PrimaryButton onClick={handlePreferencesChange}>
                Save Preferences
              </PrimaryButton>
            </ButtonGroup>
          </PreferencesSection>
        );

      default:
        return null;
    }
  };

  return (
    <ProfileContainer>
      <ProfileContent>
        <TitleSection>
          <Title>Your Profile</Title>
          <Subtitle>Manage your profile, security settings and preferences</Subtitle>
        </TitleSection>

        <TabContainer>
          <TabList>
            <TabButton 
              isActive={activeTab === 'information'}
              onClick={() => setActiveTab('information')}
            >
              Profile Information
            </TabButton>
            <TabButton 
              isActive={activeTab === 'password'}
              onClick={() => setActiveTab('password')}
            >
              Password & Security
            </TabButton>
            <TabButton 
              isActive={activeTab === 'preferences'}
              onClick={() => setActiveTab('preferences')}
            >
              Preferences
            </TabButton>
          </TabList>
        </TabContainer>

        <TabContent>
          {renderTabContent()}
        </TabContent>
      </ProfileContent>
    </ProfileContainer>
  );
};

export default Profile;
